import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Chip } from './ui/chip';
import { EmptyState } from './ui/empty-state';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { ArrowRight, CheckCircle2, CreditCard, PackageCheck, Plus, Sparkles, Trash2 } from 'lucide-react';
import FoodEducationSheetModal from './FoodEducationSheetModal';
import { getFoodEducationSheet, inferNutritionReference } from '../services/foodEducationService';
import { trackAnalyticsEvent } from '../services/analyticsService';
import { CIQUAL_ESSENTIAL_FOODS, findCiqualFoodByName } from '../data/ciqualEssentialFoods';

const CATEGORY_LABELS = {
  alimentation: 'Alimentation',
  boisson: 'Boisson',
  laitier: 'Produits laitiers',
  boulangerie: 'Boulangerie',
  viande_poisson: 'Viande & Poisson',
  plats_prepares: 'Plats préparés',
  desserts: 'Desserts & Sucreries',
  conserve: 'Conserves',
  hygiene: 'Hygiene',
  entretien: 'Entretien',
  fruits_legumes: 'Fruits et legumes',
  vetement: 'Vetements',
};

const FORBIDDEN_TERMS = ['porc', 'jambon', 'lardon', 'bacon', 'charcuterie', 'alcool', 'vin', 'biere', 'whisky', 'vodka'];

const FILTER_TAGS = [
  { key: 'vegan', label: '#Vegan' },
  { key: 'protein', label: '#RicheEnProteines' },
  { key: 'lowCarb', label: '#GlucidesBas' },
  { key: 'freezer', label: '#Congelateur' },
  { key: 'fridge', label: '#Refrigerateur' },
];

function textMatchesForbidden(value) {
  const text = String(value || '').toLowerCase();
  return FORBIDDEN_TERMS.some((term) => text.includes(term));
}

function inferItemTags(item) {
  const name = String(item?.nom || item?.item_nom || '').toLowerCase();
  const category = String(item?.categorie || '').toLowerCase();
  const tags = new Set(Array.isArray(item?.tags) ? item.tags : []);

  const veganHints = ['legume', 'fruit', 'avocat', 'amande', 'ail', 'agrumes', 'salade'];
  const proteinHints = ['oeuf', 'thon', 'anchois', 'yaourt', 'fromage', 'boeuf', 'poulet', 'amande'];
  const lowCarbHints = ['oeuf', 'avocat', 'amande', 'anchois', 'ail', 'cacahuete'];
  const fridgeHints = ['avocat', 'beurre', 'boeuf', 'lait', 'mangue', 'margarine', 'fromage', 'yaourt'];
  const freezerHints = ['surgel', 'glace', 'poisson', 'viande', 'petit pois'];

  if (veganHints.some((hint) => name.includes(hint)) || category === 'fruits_legumes') tags.add('vegan');
  if (proteinHints.some((hint) => name.includes(hint))) tags.add('protein');
  if (lowCarbHints.some((hint) => name.includes(hint))) tags.add('lowCarb');
  if (fridgeHints.some((hint) => name.includes(hint)) || ['laitier'].includes(category)) tags.add('fridge');
  if (freezerHints.some((hint) => name.includes(hint))) tags.add('freezer');

  return tags;
}

function matchesActiveFilters(item, activeFilters) {
  if (!activeFilters.length) return true;
  const tags = inferItemTags(item);
  return activeFilters.every((filterKey) => tags.has(filterKey));
}

function ShoppingPlannerTab({
  recommendations,
  suggestionGroups = [],
  items,
  toBuyItems = [],
  budgetSummary,
  purchaseTransition,
  theme,
  activeList: controlledActiveList,
  onActiveListChange,
  getSuggestionEmoji,
  onPrefillSuggestion,
  onAddSuggestionDirect,
  onOpenAddModal,
  onQuickPurchase,
  onDeleteToBuyItem,
  onClearToBuyList,
  onOpenStock,
  onOpenBudget,
  actionState = {},
}) {
  const [highlightedItem, setHighlightedItem] = useState(null);
  const [uncontrolledActiveList, setUncontrolledActiveList] = useState('suggestions');
  const [activeFilters, setActiveFilters] = useState([]);
  const [educationSheet, setEducationSheet] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const activeList = controlledActiveList ?? uncontrolledActiveList;
  const setActiveList = onActiveListChange ?? setUncontrolledActiveList;

  const suggestions = useMemo(() => {
    const stockedNames = new Set(items.map((item) => String(item.nom || '').trim().toLowerCase()));
    const mergedSuggestions = [...(Array.isArray(recommendations) ? recommendations : [])];
    const existing = new Set(mergedSuggestions.map((entry) => String(entry?.nom || '').trim().toLowerCase()));
    CIQUAL_ESSENTIAL_FOODS.forEach((food) => {
      const key = String(food.nom || '').trim().toLowerCase();
      if (!existing.has(key)) {
        mergedSuggestions.push(food);
        existing.add(key);
      }
    });

    return mergedSuggestions
      .filter((suggestion) => !stockedNames.has(String(suggestion.nom || '').trim().toLowerCase()))
      .filter((suggestion) => !textMatchesForbidden(suggestion.nom))
      .filter((suggestion) => matchesActiveFilters(suggestion, activeFilters))
      .sort((left, right) => String(left?.nom || '').localeCompare(String(right?.nom || ''), 'fr', { sensitivity: 'base' }));
  }, [items, recommendations, activeFilters]);

  const visibleToBuyItems = useMemo(() => {
    return toBuyItems
      .filter((item) => !textMatchesForbidden(item.item_nom || item.nom))
      .filter((item) => matchesActiveFilters(item, activeFilters));
  }, [toBuyItems, activeFilters]);

  const groupedSuggestions = useMemo(() => {
    const grouped = suggestions.reduce((acc, suggestion) => {
      const key = suggestion.group || suggestion.categorie || 'autres';
      if (!acc[key]) acc[key] = [];
      acc[key].push(suggestion);
      return acc;
    }, {});

    const orderedEntries = [];
    suggestionGroups.forEach((group) => {
      if (grouped[group.id]?.length) {
        orderedEntries.push([group.id, grouped[group.id]]);
        delete grouped[group.id];
      }
    });

    return [...orderedEntries, ...Object.entries(grouped)];
  }, [suggestions, suggestionGroups]);

  const suggestionGroupLabels = useMemo(() => {
    return suggestionGroups.reduce((acc, group) => {
      acc[group.id] = group.label;
      return acc;
    }, {});
  }, [suggestionGroups]);

  const formatAmount = (value) => `${Number(value || 0).toFixed(2)} €`;

  useEffect(() => {
    if (!purchaseTransition?.itemId) return;
    setHighlightedItem(purchaseTransition.itemId);
    const id = window.setTimeout(() => setHighlightedItem(null), 1800);
    return () => window.clearTimeout(id);
  }, [purchaseTransition]);

  const toggleFilter = (filterKey) => {
    setActiveFilters((prev) => (
      prev.includes(filterKey)
        ? prev.filter((entry) => entry !== filterKey)
        : [...prev, filterKey]
    ));
  };

  const openEducationSheet = (item) => {
    const name = item?.nom || item?.item_nom || 'Aliment';
    const ciqualFood = findCiqualFoodByName(name);
    const sheet = getFoodEducationSheet(name, {
      ...(ciqualFood?.nutrition100g || {}),
      ...inferNutritionReference(item),
    });
    setEducationSheet(sheet);
    trackAnalyticsEvent('food_sheet_viewed', { food_name: name });
  };

  return (
    <div className="grid min-w-0 gap-6" data-testid="shopping-planner-root">
      <Card className="overflow-hidden border-0 shadow-xl" style={{ background: theme?.heroBackground || 'linear-gradient(135deg,#fff7ed,#ecfccb)' }}>
        <CardContent className="p-6">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-orange-700">
            <Sparkles className="h-4 w-4" />
            Flux principal
          </div>
          <h2 className="text-2xl font-bold text-gray-900 sm:text-3xl">Parcours achats en 3 etapes</h2>
          <p className="mt-2 text-sm text-gray-700 sm:text-base">Simple et naturel: suggestion, ajout dans la liste, puis validation d achat qui met a jour stock, budget et historique.</p>

          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border p-4" style={{ borderColor: theme?.surfaceBorder, backgroundColor: theme?.accentSoft || '#fff7ed' }}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-orange-700">Etape 1</p>
              <p className="mt-2 font-semibold text-gray-900">Proposition d article</p>
              <p className="mt-1 text-sm text-gray-600">Suggestions intelligentes selon l historique et la frequence.</p>
            </div>
            <div className="rounded-2xl border p-4" style={{ borderColor: theme?.surfaceBorder, backgroundColor: 'rgba(16,185,129,0.12)' }}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">Etape 2</p>
              <p className="mt-2 font-semibold text-gray-900">Ajouter a ma liste</p>
              <p className="mt-1 text-sm text-gray-600">Nom + icone + categorie, avec action principale Marquer comme achete.</p>
            </div>
            <div className="rounded-2xl border p-4" style={{ borderColor: theme?.surfaceBorder, backgroundColor: 'rgba(34,211,238,0.12)' }}>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700">Etape 3</p>
              <p className="mt-2 font-semibold text-gray-900">Article achete</p>
              <p className="mt-1 text-sm text-gray-600">L article passe dans le stock et met a jour budget + historique automatiquement.</p>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => setActiveList('suggestions')}
              data-testid="shopping-view-suggestions"
              className={`${activeList === 'suggestions' ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-white text-gray-700 border border-orange-200 hover:bg-orange-50'}`}
            >
              Recommandations / Suggestions intelligentes
            </Button>
            <Button
              type="button"
              onClick={() => setActiveList('toBuy')}
              data-testid="shopping-view-to-buy"
              className={`${activeList === 'toBuy' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-white text-gray-700 border border-emerald-200 hover:bg-emerald-50'}`}
            >
              Liste a acheter
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {FILTER_TAGS.map((filterTag) => {
              const active = activeFilters.includes(filterTag.key);
              return (
                <Button
                  key={filterTag.key}
                  type="button"
                  variant="outline"
                  onClick={() => toggleFilter(filterTag.key)}
                  className={active ? 'border-emerald-500 bg-emerald-50 text-emerald-800' : ''}
                >
                  {filterTag.label}
                </Button>
              );
            })}
            {activeFilters.length > 0 && (
              <Button type="button" variant="ghost" onClick={() => setActiveFilters([])}>
                Reinitialiser les filtres
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {activeList === 'suggestions' ? (
        <Card className="min-w-0 shadow-lg" style={{ borderColor: theme?.surfaceBorder, backgroundColor: theme?.panelBackground }}>
          <CardHeader>
            <CardTitle className="text-gray-900">Suggestions intelligentes</CardTitle>
            <CardDescription>Liste dediee avec recommandations multi-categories.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {suggestions.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-emerald-300 bg-emerald-50 p-6 text-sm text-emerald-800">
                Aucune suggestion nouvelle pour le moment.
              </div>
            ) : (
              groupedSuggestions.map(([groupKey, values]) => (
                <div key={groupKey} className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">{suggestionGroupLabels[groupKey] || CATEGORY_LABELS[groupKey] || groupKey}</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    {values.map((suggestion) => (
                      <div key={suggestion.nom} className="min-w-0 rounded-2xl border bg-white/90 p-3 shadow-sm" style={{ borderColor: theme?.surfaceBorder }}>
                        <div className="mb-2 flex items-center gap-2">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-2xl">
                            {getSuggestionEmoji(suggestion)}
                          </div>
                          <button
                            type="button"
                            onClick={() => openEducationSheet(suggestion)}
                            className="min-w-0 break-words text-left text-sm font-semibold text-gray-900 underline-offset-2 hover:underline"
                          >
                            <p>{suggestion.nom}</p>
                          </button>
                        </div>
                        <p className="mb-3 text-xs text-gray-600">{CATEGORY_LABELS[suggestion.categorie] || suggestion.categorie}</p>
                        <div className="grid grid-cols-1 gap-2">
                          <Button
                            type="button"
                            onClick={() => onAddSuggestionDirect?.(suggestion) || onPrefillSuggestion(suggestion)}
                            className="h-9 rounded-xl bg-orange-600 px-3 text-xs text-white hover:bg-orange-700"
                          >
                            <Plus className="mr-1 h-3.5 w-3.5" />
                            Ajouter instantanement
                          </Button>
                          <Button type="button" variant="outline" onClick={() => openEducationSheet(suggestion)} className="h-9 rounded-xl px-3 text-xs">
                            Fiche nutritive
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="min-w-0 shadow-lg" style={{ borderColor: theme?.surfaceBorder, backgroundColor: theme?.panelBackground }}>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <CardTitle className="text-gray-900">Liste a acheter</CardTitle>
                <CardDescription>Supprimer, ajouter manuellement, ou marquer comme achete.</CardDescription>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Chip tone="primary" data-testid="shopping-count-chip">
                  {visibleToBuyItems.length} article(s)
                </Chip>
                <Button
                  type="button"
                  variant="outline"
                  onClick={onOpenAddModal}
                  className="rounded-2xl"
                  data-testid="shopping-manual-add"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Ajouter un article
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => setShowClearDialog(true)}
                  className="rounded-2xl"
                  disabled={!visibleToBuyItems.length || actionState?.clearingToBuy}
                  data-testid="shopping-clear-list"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {actionState?.clearingToBuy ? 'Vidage...' : 'Vider la liste'}
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {visibleToBuyItems.length === 0 ? (
              <EmptyState
                title="Liste vide"
                description="Ajoute des articles depuis les suggestions pour preparer tes achats."
              />
            ) : (
              visibleToBuyItems.map((item) => {
                const isAnimated = highlightedItem === item.item_id || purchaseTransition?.itemId === item.item_id;
                return (
                  <div
                    key={item.item_id}
                    className={`min-w-0 rounded-2xl border bg-white p-4 transition-all duration-500 ${isAnimated ? 'scale-[0.98] border-emerald-300 shadow-lg' : ''}`}
                    style={{ borderColor: isAnimated ? '#86efac' : theme?.surfaceBorder }}
                  >
                    {isAnimated && (
                      <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold text-emerald-700 animate-in slide-in-from-left-6 fade-in-0 duration-500">
                        <span className="rounded-full bg-orange-100 px-2 py-1 text-orange-700">Liste</span>
                        <ArrowRight className="h-3 w-3" />
                        <span className="rounded-full bg-emerald-100 px-2 py-1">Stock</span>
                        <ArrowRight className="h-3 w-3" />
                        <span className="rounded-full bg-cyan-100 px-2 py-1 text-cyan-700">Budget</span>
                      </div>
                    )}
                    <div className="flex min-w-0 flex-col items-stretch gap-3 sm:flex-row sm:items-center">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-2xl">
                        {getSuggestionEmoji(item)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <button
                          type="button"
                          onClick={() => openEducationSheet(item)}
                          className="break-words text-left font-semibold text-gray-900 underline-offset-2 hover:underline"
                        >
                          {item.item_nom}
                        </button>
                        <p className="text-xs text-gray-600">{CATEGORY_LABELS[item.categorie] || item.categorie}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => onDeleteToBuyItem(item)}
                          disabled={actionState?.deletingItemId === item.item_id}
                          className="rounded-xl border-red-200 text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {actionState?.deletingItemId === item.item_id ? 'Suppression...' : 'Supprimer'}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => {
                            setHighlightedItem(item.item_id);
                            onQuickPurchase(item.item_id);
                          }}
                          disabled={actionState?.purchasingItemId === item.item_id}
                          className={`rounded-xl text-white ${isAnimated ? 'bg-emerald-700 scale-105' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                        >
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          {actionState?.purchasingItemId === item.item_id ? 'Traitement...' : 'Marquer comme achete'}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => openEducationSheet(item)}
                          className="rounded-xl"
                        >
                          Fiche nutritive
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      )}

      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Vider la liste a acheter ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action retire seulement la liste a acheter. Le stock et l&apos;historique ne sont pas modifies.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (event) => {
                event.preventDefault();
                setShowClearDialog(false);
                await onClearToBuyList?.();
              }}
            >
              Confirmer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Card className="shadow-lg" style={{ borderColor: theme?.surfaceBorder, backgroundColor: theme?.panelBackground }}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-gray-900">
            <PackageCheck className="h-5 w-5 text-emerald-600" />
            3) Article achete vers stock et budget
          </CardTitle>
          <CardDescription>La mise a jour est automatique et visible.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {purchaseTransition ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="font-semibold text-emerald-800">{purchaseTransition.itemName} est passe dans le stock foyer</p>
              <p className="mt-1 text-sm text-emerald-700">Quantite ajoutee: +{purchaseTransition.quantity}</p>
              <p className="mt-1 text-sm text-emerald-700">
                Impact budget: {purchaseTransition.estimatedPrice != null ? formatAmount(purchaseTransition.estimatedPrice) : 'en attente de prix'}
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-cyan-200 bg-white p-4 text-sm text-gray-600">
              Apres un clic sur Marquer comme achete, le passage vers le stock et l impact budget s affichent ici.
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border bg-white p-4" style={{ borderColor: theme?.surfaceBorder }}>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Depense actuelle</p>
              <p className="mt-2 text-xl font-bold text-gray-900">{formatAmount(budgetSummary?.global_spent || 0)}</p>
            </div>
            <div className="rounded-2xl border bg-white p-4" style={{ borderColor: theme?.surfaceBorder }}>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Budget restant</p>
              <p className="mt-2 text-xl font-bold text-gray-900">{formatAmount(budgetSummary?.global_remaining || 0)}</p>
            </div>
            <div className="rounded-2xl border bg-white p-4" style={{ borderColor: theme?.surfaceBorder }}>
              <p className="text-xs uppercase tracking-[0.2em] text-gray-500">Progression</p>
              <p className="mt-2 text-xl font-bold text-gray-900">{Math.round((budgetSummary?.global_ratio || 0) * 100)}%</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={onOpenStock} className="rounded-2xl">Voir le stock foyer</Button>
            <Button type="button" variant="outline" onClick={onOpenBudget} className="rounded-2xl">
              <CreditCard className="mr-2 h-4 w-4" />
              Voir budget detaille
            </Button>
          </div>
        </CardContent>
      </Card>

      <FoodEducationSheetModal open={Boolean(educationSheet)} sheet={educationSheet} onClose={() => setEducationSheet(null)} />
    </div>
  );
}

export default ShoppingPlannerTab;